
import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";

export type AppointmentCancellationChoice = "canceled" | "canceled_not_in_time";

interface Props {
  open: boolean;
  onChoose: (choice: AppointmentCancellationChoice) => void;
  onClose: () => void;
}

const KAER_PURPLE = "#861EBC";
const KAER_PURPLE_BACKGROUND = "rgba(134, 30, 188, 0.06)";

export default function AppointmentCancellationChoiceDialog({
                                                              open,
                                                              onChoose,
                                                              onClose,
                                                            }: Props) {
  const { t } = useTranslation();

  const [selectedChoice, setSelectedChoice] =
      useState<AppointmentCancellationChoice | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedChoice(null);
    }
  }, [open]);

  const options: {
    value: AppointmentCancellationChoice;
    description: string;
  }[] = [
    {
      value: "canceled",
      description: "appointment_regular_cancellation_description",
    },
    {
      value: "canceled_not_in_time",
      description: "appointment_late_cancellation_description",
    },
  ];

  return (
      <Dialog
          open={open}
          onClose={onClose}
          aria-labelledby="appointment-cancellation-choice-title"
          aria-describedby="appointment-cancellation-choice-message"
          maxWidth="sm"
          fullWidth
          PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
              },
          }}
      >
        <DialogTitle
            id="appointment-cancellation-choice-title"
            sx={{
              fontWeight: 600,
              pb: 1,
            }}
        >
          {t("appointment_late_cancellation_title")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
              id="appointment-cancellation-choice-message"
              sx={{
                mb: 3,
                lineHeight: 1.6,
              }}
          >
            {t("appointment_late_cancellation_message")}
          </DialogContentText>

          <RadioGroup
              value={selectedChoice ?? ""}
              onChange={(event) =>
                  setSelectedChoice(
                      event.target.value as AppointmentCancellationChoice
                  )
              }
              sx={{ gap: 1.5 }}
          >
            {options.map((option) => {
              const selected = selectedChoice === option.value;

              return (
                  <Box
                      key={option.value}
                      sx={{
                        border: "1.5px solid",
                        borderColor: selected ? KAER_PURPLE : "divider",
                        borderRadius: 2,
                        backgroundColor: selected
                            ? KAER_PURPLE_BACKGROUND
                            : "transparent",
                        transition:
                            "border-color 0.15s, background-color 0.15s",
                        "&:hover": {
                          borderColor: KAER_PURPLE,
                        },
                      }}
                  >
                    <FormControlLabel
                        value={option.value}
                        control={
                          <Radio
                              sx={{
                                color: "text.disabled",
                                "&.Mui-checked": {
                                  color: KAER_PURPLE,
                                },
                              }}
                          />
                        }
                        label={
                          <Box sx={{ py: 1 }}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                            >
                              {t(option.value)}
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                }}
                            >
                              {t(option.description)}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          m: 0,
                          px: 1.5,
                          py: 0.5,
                          width: "100%",
                          alignItems: "center",
                        }}
                    />
                  </Box>
              );
            })}
          </RadioGroup>
        </DialogContent>

        <DialogActions
            sx={{
              px: 3,
              pb: 2.5,
              pt: 1,
              gap: 1,
            }}
        >
          <GenericButton
              variant="outlined"
              onClick={onClose}
          >
            {t("back")}
          </GenericButton>

          <GenericButton
              disabled={!selectedChoice}
              onClick={() => {
                if (selectedChoice) {
                  onChoose(selectedChoice);
                }
              }}
          >
            {t("appointment_confirm_cancellation")}
          </GenericButton>
        </DialogActions>
      </Dialog>
  );
}
